/**
 * `<caos-checkbox-field>` — a boolean field. (Catalog: Fields & input › CheckboxField — a boolean field.)
 *
 * States: checked, unchecked, indeterminate, disabled, invalid + validation message,
 * required, accent focus ring. The box is a custom-drawn control (a real focusable
 * element with `role="checkbox"`) whose checked fill reads `--caos-color-accent-fill`
 * (design system §04 — "toggle + checkbox use the accent") and whose tick reads
 * `--caos-color-accent-contrast` (CAOS-652), so both repaint with the five-token white-label.
 * The label sits beside the box and toggles it.
 *
 * Styling seams: `::part(control)` (the row), `::part(box)`, `::part(label)`,
 * `::part(message)`. Fires a bubbling, composed `change` event; `checked` and
 * `indeterminate` reflect to attributes.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

const sheet = makeSheet(`
  :host { display: block; }

  .control {
    display: inline-flex;
    align-items: flex-start;
    gap: var(--caos-space-2);
    cursor: pointer;
    user-select: none;
  }
  :host([disabled]) .control { cursor: not-allowed; opacity: var(--caos-state-disabled-opacity); }

  .box {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-top: 1px;
    border-radius: var(--caos-radius-sm);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
    transition: background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
  }
  .box:focus-visible {
    outline: none;
    border-color: var(--caos-color-accent);
    box-shadow: var(--caos-focus-ring);
  }
  .control:hover .box { border-color: color-mix(in srgb, var(--caos-color-accent) 45%, var(--caos-color-border)); }

  .glyph { width: 12px; height: 12px; opacity: 0; }
  .glyph svg { display: block; width: 100%; height: 100%; }
  /* The dash is drawn in the box's ink, so it follows whichever fill the box is painted with. */
  .dash { display: none; width: 10px; height: 2px; border-radius: 1px; background: currentColor; }

  /* CAOS-1551 — the white glyph ink lives HERE, with the fill it is actually drawn on, rather
     than on .box where it sat beside the unchecked surface colour and read as white-on-white.
     accent-fill rather than accent so the mark clears the text floor as well as the 3:1 one
     that strictly applies to a graphical glyph, and so a checked box matches the filled buttons. */
  :host([checked]) .box,
  :host([indeterminate]) .box {
    background: var(--caos-color-accent-fill);
    border-color: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
  }
  :host([checked]:not([indeterminate])) .glyph { opacity: 1; }
  :host([indeterminate]) .glyph { opacity: 0; }
  :host([indeterminate]) .dash { display: block; }

  :host([invalid]) .box { border-color: var(--caos-color-danger); }
  /* CAOS-1551 — danger-FILL, for the same reason the checked box above reads accent-fill. The border
     keeps the plain danger hue, because a 1px line is not text. CAOS-652 — and the danger ink: this
     state used to inherit the accent rule's white through the cascade, which a brand that sets the
     two inks apart would have got wrong. */
  :host([invalid][checked]) .box,
  :host([invalid][indeterminate]) .box {
    background: var(--caos-color-danger-fill);
    border-color: var(--caos-color-danger);
    color: var(--caos-color-danger-contrast);
  }

  .label {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.4;
    color: var(--caos-color-text);
    padding-top: 1px;
  }
  .label:empty { display: none; }
  .req { color: var(--caos-color-danger); margin-left: 2px; }

  .message {
    font-family: var(--caos-font);
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
    margin-top: var(--caos-space-1);
    padding-left: calc(18px + var(--caos-space-2));
  }
  .message:empty { display: none; }
  :host([invalid]) .message { color: var(--caos-color-danger); }

  /* ── Inline (record-detail) variant — the box IS its own affordance, so the P5b
     inline mode renders it directly (no click-to-reveal); a compact box, no helper text.
     The label is suppressed (the field kicker supplies the field name). */
  :host([inline]) .message { display: none; }
  :host([inline]) .label { display: none; }
  :host([inline]) .box { width: 16px; height: 16px; }
`);
export class CaosCheckboxField extends CaosElement {
  static get observedAttributes(): string[] {
    return ['label', 'checked', 'indeterminate', 'disabled', 'required', 'invalid', 'message', 'name'];
  }

  private box: HTMLSpanElement;
  private labelEl: HTMLSpanElement;
  private reqEl: HTMLSpanElement;
  private messageEl: HTMLDivElement;

  constructor() {
    super(sheet);

    const control = document.createElement('label');
    control.className = 'control';
    control.setAttribute('part', 'control');

    this.box = document.createElement('span');
    this.box.className = 'box';
    this.box.setAttribute('part', 'box');
    this.box.setAttribute('role', 'checkbox');
    this.box.tabIndex = 0;

    // lucide "check" — built via the DOM (no innerHTML, CSP-safe).
    const glyph = document.createElement('span');
    glyph.className = 'glyph';
    glyph.setAttribute('aria-hidden', 'true');
    // CAOS-602 — the vocabulary's `check`. The heavier stroke stays: inside a small box a
    // standard-weight tick reads as faint, and weight is this component's decision to make.
    const svg = makeIcon('check');
    svg.setAttribute('stroke-width', '3');
    glyph.append(svg);

    const dash = document.createElement('span');
    dash.className = 'dash';
    dash.setAttribute('aria-hidden', 'true');
    this.box.append(glyph, dash);

    this.labelEl = document.createElement('span');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');
    this.reqEl = document.createElement('span');
    this.reqEl.className = 'req';
    this.reqEl.textContent = '*';
    this.reqEl.hidden = true;
    this.reqEl.setAttribute('aria-hidden', 'true');
    this.labelEl.append(document.createElement('slot'), this.reqEl);

    control.append(this.box, this.labelEl);

    this.messageEl = document.createElement('div');
    this.messageEl.className = 'message';
    this.messageEl.setAttribute('part', 'message');

    this.root.append(control, this.messageEl);

    // Toggling: a click anywhere on the row, or Space/Enter on the box.
    control.addEventListener('click', (e) => {
      // The box is a child of the label; a click already reaches here once.
      if (e.target !== this.box) this.toggle();
    });
    this.box.addEventListener('click', () => this.toggle());
    this.box.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** Whether the box is checked. */
  get checked(): boolean {
    return this.hasAttribute('checked');
  }
  set checked(v: boolean) {
    if (v) this.setAttribute('checked', '');
    else this.removeAttribute('checked');
  }

  get indeterminate(): boolean {
    return this.hasAttribute('indeterminate');
  }
  set indeterminate(v: boolean) {
    if (v) this.setAttribute('indeterminate', '');
    else this.removeAttribute('indeterminate');
  }

  /** Focus the box (the inline record-detail editor auto-focuses on mount). */
  override focus(options?: FocusOptions): void {
    this.box.focus(options);
  }

  private toggle(): void {
    if (this.hasAttribute('disabled')) return;
    // Indeterminate resolves to checked on the first interaction (native behaviour).
    this.removeAttribute('indeterminate');
    this.checked = !this.checked;
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private sync(): void {
    this.reqEl.hidden = !this.hasAttribute('required');
    const checked = this.hasAttribute('checked');
    const indeterminate = this.hasAttribute('indeterminate');
    this.box.setAttribute('aria-checked', indeterminate ? 'mixed' : String(checked));
    if (this.hasAttribute('disabled')) {
      this.box.setAttribute('aria-disabled', 'true');
      this.box.tabIndex = -1;
    } else {
      this.box.removeAttribute('aria-disabled');
      this.box.tabIndex = 0;
    }
    if (this.hasAttribute('invalid')) this.box.setAttribute('aria-invalid', 'true');
    else this.box.removeAttribute('aria-invalid');
    if (this.hasAttribute('required')) this.box.setAttribute('aria-required', 'true');
    else this.box.removeAttribute('aria-required');
    this.messageEl.textContent = this.getAttribute('message') ?? '';
  }
}

define('caos-checkbox-field', CaosCheckboxField);

declare global {
  interface HTMLElementTagNameMap {
    'caos-checkbox-field': CaosCheckboxField;
  }
}
